import React from 'react';
import { Link } from 'react-router-dom';
import { DelayedLoad } from '../../../../types/dashboard.types';
import Card, { cardStyles } from '../../../components/ui/Card/Card';
import styles from './AttentionCard.module.css';

/**
 * LO QUE VA TARDE, PARA EL CLIENTE (20-oct-2026).
 *
 * > *"falta algo de la columna de la derecha queda muy vacia"*
 *
 * Usa `delayedLoads`, que el API ya mandaba a todo el que tiene `LOADS` o `LD_VIEW` y nadie mostraba: cargas abiertas
 * con el *protect date* vencido (la misma regla del API, `getDelayedLoads`).
 *
 * - **Con retrasos:** los más atrasados primero, hasta 4, con cuántos días; cada uno abre la carga. Ámbar hasta 2 días,
 *   rojo desde 3: la urgencia se lee antes que el número, y el número va escrito.
 * - **Sin retrasos:** se dice, en verde. Un *"todo bien"* explícito es información; una tarjeta vacía no.
 */

interface AttentionCardProps {
  loads?: DelayedLoad[];
}

const SHOWN = 4;

const lateLabel = (days: number): string =>
  days <= 0 ? 'Late today' : `${days} ${days === 1 ? 'day' : 'days'} late`;

const AttentionCard: React.FC<AttentionCardProps> = ({ loads = [] }) => {
  const late = [...loads].sort((a, b) => (b.days_delayed || 0) - (a.days_delayed || 0));
  const extra = late.length - SHOWN;

  return (
    <Card className={[styles.card, late.length ? styles.alert : styles.calm].join(' ')}>
      <div className={cardStyles.header}>
        <h3 className={cardStyles.title}>{late.length ? 'Running late' : 'On schedule'}</h3>
        {late.length > 0 && <span className={styles.count}>{late.length}</span>}
      </div>

      {late.length === 0 ? (
        <div className={styles.ok}>
          <span className={styles.okIcon} aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M5 12.5l4.2 4.2L19 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div>
            <p className={styles.okTitle}>All on schedule</p>
            <p className={styles.okText}>None of your open loads is past its protect date.</p>
          </div>
        </div>
      ) : (
        <>
          <ul className={styles.list}>
            {late.slice(0, SHOWN).map((load) => {
              const days = load.days_delayed || 0;
              return (
                <li key={load.id}>
                  <Link
                    className={styles.row}
                    to={`/guide/detail/${load.id}`}
                    style={{ '--sev': days >= 3 ? '#ef4444' : '#f5a623' } as React.CSSProperties}
                  >
                    <span className={styles.main}>
                      <span className={styles.ref}>{load.dylo_reference || load.pro_number || 'No reference'}</span>
                      <span className={styles.route}>
                        {load.origin || '—'} → {load.destiny || '—'}
                      </span>
                    </span>
                    <span className={styles.late}>{lateLabel(days)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
          {extra > 0 && (
            <Link className={styles.more} to="/loads">
              +{extra} more in Loads
            </Link>
          )}
        </>
      )}
    </Card>
  );
};

export default AttentionCard;
